import { ShortCode } from '@highereducation/wordpress';
import { Split, SplitItem, type SplitRatio } from '@highereducation/hen-ui';

interface SplitShortcodeProps {
  gap?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';
  breakpoint?: 'sm' | 'md' | 'lg' | 'xl';
  ratio?: SplitRatio;
  reverse?: 'true' | 'false';
  children?: React.ReactNode;
  [key: string]: unknown;
}

/**
 * SplitShortcode - Shortcode wrapper for Split layout component
 *
 * Responsive two-column layout that stacks on mobile.
 * Replaces manual `<div class="flex flex-col md:flex-row gap-*">` patterns.
 *
 * Supported shortcode attributes:
 * - gap: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' (default: 'xl')
 * - breakpoint: 'sm' | 'md' | 'lg' | 'xl' (default: 'md')
 * - ratio: 'equal' | '1/3-2/3' | '2/3-1/3' | '1/4-3/4' | '3/4-1/4' | '1.3-1' | '4/9-5/9' | '5/9-4/9' (default: 'equal')
 * - reverse: 'true' | 'false' (default: 'false')
 *
 * @example WordPress shortcode usage:
 * [hen-ui-split gap="3xl" ratio="equal"]
 *   [hen-ui-split-item]
 *     [hen-ui-image-card ...]...[/hen-ui-image-card]
 *   [/hen-ui-split-item]
 *   [hen-ui-split-item]
 *     [hen-ui-image-card ...]...[/hen-ui-image-card]
 *   [/hen-ui-split-item]
 * [/hen-ui-split]
 */
export const SplitShortcode: ShortCode<SplitShortcodeProps> = {
  component: ({
    gap = 'xl',
    breakpoint = 'md',
    ratio = 'equal',
    reverse = 'false',
    children,
  }) => {
    return (
      <Split
        gap={gap}
        breakpoint={breakpoint}
        ratio={ratio}
        reverse={reverse === 'true'}
      >
        {children}
      </Split>
    );
  },
};

interface SplitItemShortcodeProps {
  children?: React.ReactNode;
  [key: string]: unknown;
}

/**
 * SplitItemShortcode - Shortcode wrapper for SplitItem component
 *
 * Child component for Split layout. Use as direct children of Split.
 *
 * @example WordPress shortcode usage:
 * [hen-ui-split-item]
 *   ...content...
 * [/hen-ui-split-item]
 */
export const SplitItemShortcode: ShortCode<SplitItemShortcodeProps> = {
  component: ({ children }) => {
    return <SplitItem>{children}</SplitItem>;
  },
};
